<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useCostCenterStore } from '../../stores/costCenter'
import { useCurrentOrganizationStore } from '../../stores/currentOrganization'
import ConfirmDialog from '../../components/common/ConfirmDialog.vue'
import EditModal from '../../components/common/EditModal.vue'
import CostTypeModal from '../../components/cost-center/CostTypeModal.vue'

const costCenterStore = useCostCenterStore()
const currentOrganizationStore = useCurrentOrganizationStore()
const showNewCenterModal = ref(false)
const showEditModal = ref(false)
const showEditTypeModal = ref(false)
const showNewTypeModal = ref(false)
const showConfirmDelete = ref(false)
const selectedCenter = ref<any>(null)
const selectedType = ref<any>(null)
const editingCenter = ref<any>(null)
const itemToDelete = ref<{ type: 'center' | 'type', id: string, centerId?: string } | null>(null)
const expandedCenters = ref<Set<string>>(new Set())

const newCostCenter = ref({
  code: '',
  name: '',
  description: ''
})

const hasOrganization = computed(() => {
  return !!currentOrganizationStore.currentOrganization?.id
})

const sortedCostCenters = computed(() => {
  return [...costCenterStore.costCenters].sort((a, b) => {
    const codeA = parseInt(a.code) || 0
    const codeB = parseInt(b.code) || 0
    return codeA - codeB
  })
})

const isLoading = ref(false)

onMounted(async () => {
  isLoading.value = true
  try {
    if (currentOrganizationStore.currentOrganization?.id) {
      await costCenterStore.fetchCostCenters(currentOrganizationStore.currentOrganization.id)
    }
  } catch (error) {
    console.error('Error loading cost centers:', error)
  } finally {
    isLoading.value = false
  }
})

// Watch for changes in current organization
watch(() => currentOrganizationStore.currentOrganization?.id, async (newOrgId) => {
  if (newOrgId) {
    isLoading.value = true
    try {
      await costCenterStore.fetchCostCenters(newOrgId)
    } catch (error) {
    console.error('Error loading cost centers:', error)
  } finally {
    isLoading.value = false
  }
  }
})

const toggleCenter = async (centerId: string) => {
  if (expandedCenters.value.has(centerId)) {
    expandedCenters.value.delete(centerId)
  } else {
    expandedCenters.value.add(centerId)
  }
}

const isCenterExpanded = (centerId: string) => {
  return expandedCenters.value.has(centerId)
}

const getNextCenterCode = () => {
  if (costCenterStore.costCenters.length === 0) return '1'
  const codes = costCenterStore.costCenters
    .map(c => parseInt(c.code))
    .filter(code => !isNaN(code))
  const maxCode = Math.max(...codes, 0)
  return (maxCode + 1).toString()
}

const addCostCenter = async () => {
  if (!currentOrganizationStore.currentOrganization?.id) {
    console.error('No organization selected')
    return
  }

  const code = getNextCenterCode()
  const success = await costCenterStore.addCostCenter({
    ...newCostCenter.value,
    code,
    organizationId: currentOrganizationStore.currentOrganization.id
  })

  if (success) {
    showNewCenterModal.value = false
    newCostCenter.value = {
      code: '',
      name: '',
      description: ''
    }
  }
}

const getNextTypeCode = (center: any) => {
  if (!center || !center.types) return `${center.code}.1`
  
  // Filtra apenas os tipos que seguem o padrão código.número
  const typeNumbers = center.types
    .map(t => {
      const match = t.code.match(new RegExp(`^${center.code}\\.(\\d+)$`))
      return match ? parseInt(match[1]) : 0
    })
    .filter(n => n > 0)

  // Se não houver tipos ou nenhum seguir o padrão, começa com .1
  if (typeNumbers.length === 0) return `${center.code}.1`

  // Pega o maior número e adiciona 1
  const nextNumber = Math.max(...typeNumbers) + 1
  return `${center.code}.${nextNumber}`
}

const openNewTypeModal = (center: any) => {
  selectedCenter.value = center
  // Gera o próximo código automaticamente
  const nextCode = getNextTypeCode(center)
  showNewTypeModal.value = true
  // Passa o código gerado para o modal
  nextTypeCode.value = nextCode
}

// Ref para armazenar o próximo código
const nextTypeCode = ref('')

const addCostType = async (type: any) => {
  if (!selectedCenter.value?.id) {
    console.error('No center selected')
    return
  }

  try {
    // Verifica se já existe um tipo com o mesmo código
    const center = costCenterStore.costCenters.find(c => c.id === selectedCenter.value?.id)
    if (center?.types?.some(t => t.code === type.code)) {
      throw new Error('Já existe um tipo de custo com este código')
    }

    const success = await costCenterStore.addCostType(
      selectedCenter.value.id,
      type
    )

    if (success) {
      showNewTypeModal.value = false
      selectedCenter.value = null
      alert('Tipo de custo adicionado com sucesso!')
    }
  } catch (error: any) {
    console.error('Erro ao adicionar tipo de custo:', error)
    alert(error.message || 'Erro ao adicionar tipo de custo. Tente novamente.')
  }
}

// Funções de edição
const startEditCenter = (center: any) => {
  editingCenter.value = { ...center }
  showEditModal.value = true
}

const startEditType = (type: any, center: any) => {
  selectedType.value = { ...type }
  selectedCenter.value = center
  showEditTypeModal.value = true
}

const saveEditCenter = async () => {
  if (!editingCenter.value?.id) return

  try {
    const success = await costCenterStore.updateCostCenter(
      editingCenter.value.id,
      editingCenter.value
    )

    if (success) {
      showEditModal.value = false
      editingCenter.value = null
      alert('Centro de custo atualizado com sucesso!')
    }
  } catch (error: any) {
    console.error('Erro ao atualizar centro de custo:', error)
    alert(error.message || 'Erro ao atualizar centro de custo. Tente novamente.')
  }
}

const saveEditType = async () => {
  if (!selectedType.value?.id || !selectedCenter.value?.id) return

  try {
    const success = await costCenterStore.updateCostType(
      selectedCenter.value.id,
      selectedType.value.id,
      selectedType.value
    )

    if (success) {
      showEditTypeModal.value = false
      selectedType.value = null
      selectedCenter.value = null
      alert('Tipo de custo atualizado com sucesso!')
    }
  } catch (error: any) {
    console.error('Erro ao atualizar tipo de custo:', error)
    alert(error.message || 'Erro ao atualizar tipo de custo. Tente novamente.')
  }
}

// Funções de exclusão
const confirmDelete = (type: 'center' | 'type', id: string, centerId?: string) => {
  itemToDelete.value = { type, id, centerId }
  showConfirmDelete.value = true
}

const handleDelete = async () => {
  if (!itemToDelete.value) return

  try {
    const isCenter = itemToDelete.value.type === 'center'
    let success = false

    if (isCenter) {
      success = await costCenterStore.deleteCostCenter(itemToDelete.value.id)
    } else if (itemToDelete.value.centerId) {
      success = await costCenterStore.deleteCostType(
        itemToDelete.value.centerId,
        itemToDelete.value.id
      )
    }

    if (success) {
      showConfirmDelete.value = false
      itemToDelete.value = null
      alert(isCenter ? 'Centro de custo excluído com sucesso!' : 'Tipo de custo excluído com sucesso!')
    }
  } catch (error: any) {
    console.error('Erro ao excluir:', error)
    alert(error.message || 'Ocorreu um erro ao tentar excluir. Tente novamente.')
  } finally {
    showConfirmDelete.value = false
    itemToDelete.value = null
  }
}
</script>

<template>
  <div class="p-6">
    <!-- Loading State -->
    <div v-if="isLoading" class="text-center py-8">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-dark mx-auto"></div>
      <p class="mt-4 text-gray-600">Carregando centros de custo...</p>
    </div>

    <!-- No Organization Selected -->
    <div v-else-if="!hasOrganization" class="text-center py-8">
      <p class="text-gray-600">Por favor, selecione uma organização para continuar.</p>
    </div>

    <!-- Main Content -->
    <div v-else>
      <div class="flex justify-between items-center mb-6">
        <div>
          <h1 class="text-2xl font-bold mb-2">Centro de Custos</h1>
          <p class="text-gray-600">Gerencie seus centros de custo e categorias</p>
        </div>
        <button
          @click="showNewCenterModal = true"
          class="bg-primary-dark text-white px-4 py-2 rounded-lg hover:bg-primary-dark/90"
        >
          Novo Centro de Custo
        </button>
      </div>

      <!-- Lista de Centros de Custo -->
      <div class="space-y-4">
        <div v-for="center in sortedCostCenters" :key="center.id" class="bg-white rounded-lg shadow">
          <!-- Accordion Header -->
          <div 
            class="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            @click="toggleCenter(center.id!)"
          >
            <div class="flex items-center space-x-4">
              <i 
                :class="[
                  'pi text-gray-500 transition-transform',
                  isCenterExpanded(center.id!) ? 'pi-chevron-down' : 'pi-chevron-right'
                ]"
              ></i>
              <div>
                <h3 class="font-medium">
                  <span class="text-primary-dark mr-2">{{ center.code }}</span>
                  {{ center.name }}
                </h3>
                <p class="text-sm text-gray-600">{{ center.description }}</p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <button
                @click.stop="startEditCenter(center)"
                class="text-primary-dark hover:text-primary-dark/80 p-2"
              >
                <i class="pi pi-pencil"></i>
              </button>
              <button
                @click.stop="confirmDelete('center', center.id!)"
                class="text-red-600 hover:text-red-900 p-2"
              >
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </div>

          <!-- Accordion Content -->
          <div 
            v-show="isCenterExpanded(center.id!)"
            class="border-t"
          >
            <div class="p-4">
              <div class="flex justify-between items-center mb-4">
                <h4 class="font-medium text-gray-700">Tipos de Custo</h4>
                <button
                  @click="openNewTypeModal(center)"
                  class="text-primary-dark hover:text-primary-dark/80"
                >
                  <i class="pi pi-plus mr-1"></i>
                  Adicionar Tipo
                </button>
              </div>

              <div class="space-y-3">
                <div
                  v-for="type in center.types || []"
                  :key="type.id"
                  class="bg-gray-50 p-3 rounded-lg"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <h5 class="font-medium">
                        <span class="text-primary-dark mr-2">{{ type.code }}</span>
                        {{ type.name }}
                      </h5>
                      <p class="text-sm text-gray-600">{{ type.description }}</p>
                    </div>
                    <div class="space-x-2">
                      <button
                        @click="startEditType(type, center)"
                        class="text-primary-dark hover:text-primary-dark/80"
                      >
                        <i class="pi pi-pencil"></i>
                      </button>
                      <button
                        @click="confirmDelete('type', type.id!, center.id)"
                        class="text-red-600 hover:text-red-900"
                      >
                        <i class="pi pi-trash"></i>
                      </button>
                    </div>
                  </div>
                </div>
                <div v-if="!(center.types || []).length" class="text-gray-500 text-sm text-center py-4">
                  Nenhum tipo de custo cadastrado
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Modal Novo Centro de Custo -->
      <div v-if="showNewCenterModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div class="bg-white p-6 rounded-lg w-full max-w-md">
          <h2 class="text-xl font-bold mb-4">Novo Centro de Custo</h2>
          <form @submit.prevent="addCostCenter" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">Nome</label>
              <input
                v-model="newCostCenter.name"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Descrição</label>
              <textarea
                v-model="newCostCenter.description"
                rows="3"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              ></textarea>
            </div>
            <div class="flex justify-end space-x-3">
              <button
                type="button"
                @click="showNewCenterModal = false"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90"
                :disabled="costCenterStore.loading"
              >
                <i v-if="costCenterStore.loading" class="pi pi-spin pi-spinner mr-2"></i>
                Adicionar
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Modal Tipo de Custo -->
      <CostTypeModal
        v-if="showNewTypeModal"
        :center="selectedCenter"
        :initial-code="nextTypeCode"
        @close="showNewTypeModal = false"
        @add="addCostType"
      />

      <!-- Edit Modal -->
      <EditModal
        v-if="showEditModal"
        :show="showEditModal"
        title="Editar Centro de Custo"
        @close="showEditModal = false"
        @save="saveEditCenter"
      >
        <form @submit.prevent="saveEditCenter" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Nome</label>
            <input
              v-model="editingCenter.name"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Descrição</label>
            <textarea
              v-model="editingCenter.description"
              rows="3"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            ></textarea>
          </div>
        </form>
      </EditModal>

      <!-- Edit Type Modal -->
      <EditModal
        v-if="showEditTypeModal"
        :show="showEditTypeModal"
        title="Editar Tipo de Custo"
        @close="showEditTypeModal = false"
        @save="saveEditType"
      >
        <form @submit.prevent="saveEditType" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">Nome</label>
            <input
              v-model="selectedType.name"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Descrição</label>
            <textarea
              v-model="selectedType.description"
              rows="3"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            ></textarea>
          </div>
        </form>
      </EditModal>

      <!-- Confirm Delete Dialog -->
      <ConfirmDialog
        v-if="showConfirmDelete"
        :show="showConfirmDelete"
        :title="itemToDelete?.type === 'center' ? 'Excluir Centro de Custo' : 'Excluir Tipo de Custo'"
        :message="itemToDelete?.type === 'center' 
          ? 'Tem certeza que deseja excluir este centro de custo? Todos os tipos de custo associados também serão excluídos.'
          : 'Tem certeza que deseja excluir este tipo de custo?'"
        @confirm="handleDelete"
        @cancel="showConfirmDelete = false"
      >
        <template #icon>
          <i class="pi pi-exclamation-triangle text-red-500 text-xl"></i>
        </template>
      </ConfirmDialog>
    </div>
  </div>
</template>

<style scoped>
.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.3s ease-out;
  overflow: hidden;
}

.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
}

.accordion-enter-to,
.accordion-leave-from {
  max-height: 500px;
}
</style>